/**
 * FontLoadingValidator - Utility for validating font loading results
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility validates font loading results and identifies failed fonts
 * for error reporting and debugging.
 */

import type { FontData } from '../../../core/types/Typesetter';

/**
 * Result of font loading validation
 */
export interface FontLoadingValidationResult {
  allLoaded: boolean;
  failedFonts: string[];
  loadedFonts: string[];
  missingCount: number;
}

/**
 * Validate font loading results
 * 
 * Compares loaded fonts against requested fonts to identify any failures.
 * 
 * @param loadedFonts - Array of fonts that were successfully loaded
 * @param requestedFontUids - Set or array of font UIDs that were requested
 * @returns Validation result with failed fonts and loaded fonts
 */
export function validateFontLoading(
  loadedFonts: FontData[],
  requestedFontUids: Set<string> | string[] | Map<string, any>
): FontLoadingValidationResult {
  // Convert requested fonts to Set for easy lookup
  const requestedSet = requestedFontUids instanceof Set 
    ? requestedFontUids 
    : requestedFontUids instanceof Map
    ? new Set(requestedFontUids.keys())
    : new Set(requestedFontUids);
  
  // Get loaded font UIDs
  const loadedUids = new Set(loadedFonts.map(f => f.font_uid));
  
  // Find failed fonts (requested but not loaded)
  const failedFonts: string[] = [];
  for (const uid of requestedSet) {
    if (!loadedUids.has(uid)) {
      failedFonts.push(uid);
    }
  }
  
  return {
    allLoaded: failedFonts.length === 0,
    failedFonts,
    loadedFonts: Array.from(loadedUids),
    missingCount: failedFonts.length
  };
}

/**
 * Create error message for font loading failures
 * 
 * @param validationResult - Result from validateFontLoading
 * @param baseError - Optional base error message
 * @returns Formatted error message
 */
export function createFontLoadingErrorMessage(
  validationResult: FontLoadingValidationResult,
  baseError?: string
): string {
  if (validationResult.allLoaded) {
    return baseError || 'Font loading completed successfully';
  }
  
  const parts: string[] = [];
  if (baseError) {
    parts.push(baseError);
  }
  parts.push(`${validationResult.missingCount} font(s) failed to load: ${validationResult.failedFonts.join(', ')}`);
  parts.push(`Loaded fonts: ${validationResult.loadedFonts.join(', ')}`);
  
  return parts.join('. ');
}

